<template>
  <div>
    <div
      v-if="permissions.includes('create_merchant')"
      class="mb-3 flex justify-end"
    >
      <RouterLink
        #default="{ href, navigate }"
        :to="{ name: 'merchantCreate' }"
        custom
      >
        <ActionButton as="a" :href="href" @click="navigate">
          Create New Merchant
        </ActionButton>
      </RouterLink>
    </div>
    <BaseCard class="relative">
      <QueryError
        v-if="isError"
        class="min-h-72"
        :show-retry="true"
        @retry="refetchMerchants"
      >
        Failed to retrieve merchants
      </QueryError>
      <template v-else>
        <div
          class="mb-2 flex w-full flex-col justify-center gap-4 md:flex-row"
        >
          <div class="flex-1"> </div>
          <SearchInput v-model="rawSearch" />
          <div
            class="flex flex-1 items-center justify-center md:justify-end"
          >
            <FilterCheckbox
              v-model="
                reactiveFilters.includeDemoAndDisabled
              "
              label="Include demo and disabled?"
              name="includeDemoAndDisabled"
            />
          </div>
        </div>
        <BaseTable
          v-model:ordering="ordering"
          :rows="data?.items ?? []"
          :columns="columns"
          :is-loading="isFetching"
        >
          <template
            #cell-criticalEngagement="{ value, row }"
          >
            <template v-if="row.isCriticalAlertsSupported">
              <template v-if="value.length > 0">
                <IconFaceFrown class="size-6 text-error" />
              </template>
              <template v-else>
                <IconFaceSmile
                  class="size-6 text-positive"
                />
              </template>
            </template>
            <div v-else class="size-6"></div>
          </template>

          <template #cell-engagementScore="{ value }">
            <template
              v-if="value !== null && value !== undefined"
            >
              <div
                class="ml-2 flex size-8 shrink-0 items-center justify-center rounded-full border-2 border-info font-semibold text-info"
              >
                {{ value.toFixed(1) }}
              </div>
            </template>
          </template>
          <template #cell-merchantName="{ row }">
            <BaseTableChevronLink
              :to="{
                name: 'merchantDetail',
                params: {
                  tenantId: row.id,
                },
              }"
            >
              <p
                :class="
                  row.isActive
                    ? 'text-background-fg'
                    : 'text-background-fg/60'
                "
              >
                {{ row.merchantName }}
                <span v-if="!row.isActive">(disabled)</span>
              </p>
              <p
                class="text-xs font-medium text-background-fg/50"
              >
                {{ row.merchantId }}
              </p>
            </BaseTableChevronLink>
          </template>
          <template #cell-lastTransactedAt="{ row, value }">
            <template v-if="row.lastTransactedAt">
              <div>
                <p class="text-sm text-background-fg/87">
                  {{ formatToDate(row.lastTransactedAt) }}
                  at
                  {{ formatToTime(row.lastTransactedAt) }}
                </p>
                <p class="text-xs text-background-fg/70">
                  {{ value }}
                </p>
              </div>
            </template>
          </template>
          <template #cell-portalLink="{ row }">
            <BaseTableButton
              v-if="row.portalLink"
              :href="row.portalLink"
              target="_blank"
              variant="info"
            >
              Open Portal
            </BaseTableButton>

            <BaseTableButton
              v-else-if="row.beepaidgoLink"
              :href="row.beepaidgoLink"
              target="_blank"
              variant="info"
            >
              Open BeepaidGo
            </BaseTableButton>
          </template>
        </BaseTable>

        <div class="flex flex-col py-2 md:flex-row">
          <div
            v-if="!!statsRefreshedAt"
            class="ml-2 text-xs text-black/40"
          >
            Merchant stats last refreshed at
            {{ statsRefreshedAt }}
          </div>
          <BasePagination
            v-model="page"
            :page-count="pageCount"
            class="ml-auto"
          />
        </div>
      </template>
    </BaseCard>
  </div>

  <RouterView #default="{ Component, route }">
    <BaseDialog
      :is-open="!!Component"
      size="lg"
      @close="router.push({ name: 'merchantIndex' })"
    >
      <component :is="Component" v-bind="route.params" />
    </BaseDialog>
  </RouterView>
</template>

<script setup lang="ts">
import {
  type TableOrdering,
  type TableColumn,
} from "@/components/BaseTable.vue"
import type {
  ListMerchantsOrdering,
  Merchant,
  MerchantFilter,
} from "@/services/merchants/listMerchants"

const router = useRouter()
const { permissions } = useAuth()
const rawSearch = ref("")
const debouncedSearch = debouncedRef(rawSearch, 300)

const reactiveFilters = reactive({
  includeDemoAndDisabled: false,
})
const filters = computed<MerchantFilter>(() => ({
  search: debouncedSearch.value.trim(),
  ...reactiveFilters,
}))

const { page, pageCount, pagination, resetPagination } =
  usePagination({
    count: computed(() => data.value?.count || 0),
    limit: 10,
  })

const ordering = ref<
  TableOrdering<ListMerchantsOrdering> | undefined
>()

watch(
  () => filters,
  () => resetPagination(),
  { deep: true }
)
const query = reactiveComputed(() => ({
  ...filters.value,
  ordering: ordering.value,
  ...pagination.value,
}))
const {
  data,
  isFetching,
  isError,
  refetch: refetchMerchants,
} = useMerchants(query)

const statsRefreshedAt = computed(
  () =>
    data.value &&
    data.value.context.merchantStatsRefreshedAt &&
    DateFns.format(
      data.value.context.merchantStatsRefreshedAt,
      "HH:mm dd/MM/yyyy"
    )
)

const now = useNow()

type ColumnId =
  | "criticalEngagement"
  | "merchantName"
  | "engagementScore"
  | "tpReference"
  | "transactionTotal"
  | "lastTransactedAt"
  | "source"
  | "portalLink"

const columns: Array<
  TableColumn<Merchant, ColumnId, ListMerchantsOrdering>
> = [
  {
    id: "criticalEngagement",
    header: "",
    getCellValue: (row) => row.criticalEngagementAlerts,
  },
  {
    id: "merchantName",
    header: "Merchant Name",
    getCellValue: (row) => row.merchantName,
    isSortable: true,
  },

  {
    id: "engagementScore",
    header: "EScore",
    getCellValue: (row) => row.engagementScore,
    isSortable: true,
  },
  {
    id: "tpReference",
    header: "Reference",
    getCellValue: (row) => row.tpReference,
  },
  {
    id: "transactionTotal",
    header: "Total Transactions",
    getCellValue: (row) =>
      formatToCurrencyString(
        row.transactionTotal,
        row.currency
      ),
    isSortable: true,
  },
  {
    id: "lastTransactedAt",
    header: "Last Transacted At",
    getCellValue: (row) =>
      row.lastTransactedAt
        ? DateFns.formatDistance(
            row.lastTransactedAt,
            now.value,
            {
              addSuffix: true,
            }
          )
        : "",
    isSortable: true,
  },
  {
    id: "source",
    header: "Last App Used",
    getCellValue: (row) => row.sources?.at(0),
  },
  {
    id: "portalLink",
    header: "",
    getCellValue: (row) => row.portalLink,
    isSortable: false,
  },
]
</script>
